import _merge from 'lodash/merge';
import { Lightning } from '@lightningjs/sdk';
import { RENDERING_CONTEXT, safetyMargins } from '@bedrock-rdk/constants';
import Progress from '../progress/progress.component';
import { makeProgressBarProperties } from '../progress/progress.utils';
import FocusShader from '../shaders/focus.shader';
import { areContentItemsEqual, areContextsEqual, getTemplateEntry, makeTextPatch } from '../utils/templates';
import { DETACHED_SIGNAL, INACTIVE_SIGNAL, SELECTED_SIGNAL } from '../signals';
import {
  colors,
  defaultContext,
  getFocusedStyle,
  getImageSize,
  getInitialStyle,
  unFocusedStyle,
  PADDING,
  TEXT_LEFT_PADDING,
} from './horizontalSquare.style';
import { getLines } from './horizontalSquare.utils';
import { sizes, templates, imageOptions, LINE1_COLOR, LINE2_COLOR, LINE3_COLOR } from './horizontalSquare.constants';

/**
 * Horizontal Card is a 16:9 tile component with textual metadata to the right of the tile
 *
 * @see https://app.abstract.com/projects/162522cb-a770-4688-9ead-06e1fd627b20/branches/master/collections/c5080a9f-e0d4-44d9-8144-68384adb451b
 */
export default class HorizontalSquare extends Lightning.Component {
  /* istanbul ignore next */
  static _template() {
    return _merge(
      {
        Background: {
          rect: true,
          shader: {
            type: Lightning.shaders.RoundedRectangle,
          },
        },
        Border: {
          rect: true,
          shader: {
            type: FocusShader,
          },
        },
        ScalableContent: {
          shader: {
            type: Lightning.shaders.RoundedRectangle,
          },
        },
        FlexContainer: {
          Line1: {},
          Line2: {},
          Line3: {},
          ProgressBar: {
            type: Progress,
            visible: false,
          },
        },
      },
      getInitialStyle(),
    );
  }

  _construct() {
    this._index = 0;
    this._style = defaultContext;
  }

  _inactive() {
    this.signal(INACTIVE_SIGNAL, this);
  }

  _detach() {
    this._item = undefined;
    this.signal(DETACHED_SIGNAL, this);
  }

  get item() {
    return this._item;
  }

  set item({
    content,
    config,
    template,
    colorScheme = defaultContext.colorMode,
    renderingContext = RENDERING_CONTEXT.GENERAL,
  }) {
    const progress = makeProgressBarProperties(content.progress);

    if (areContentItemsEqual(this._item, content) && areContextsEqual(this._renderingContext, renderingContext)) {
      this._item = content;
      this.patch({
        FlexContainer: {
          Line3: {
            visible: !progress.visible,
          },
          ProgressBar: progress,
        },
      });

      return;
    }

    this._renderingContext = renderingContext;
    this._item = content;
    this._config = config || getTemplateEntry(sizes, template, renderingContext, this._style.screenSize);
    this._itemTemplate = template;

    const { wordWrapWidth } = this;
    const { image } = this._item;
    const imageUrl = (image && image.src) || '';
    const [line1, line2, line3] = getLines(this._item);
    const { screenSize } = this._style;

    this.patch({
      Background: {
        w: this._config.w,
        h: this._config.h,
      },
      ScalableContent: {
        src: imageUrl,
        shader: {
          strokeColor: colors.border[colorScheme],
        },
      },
      FlexContainer: {
        Line1: makeTextPatch(line1, wordWrapWidth, LINE1_COLOR[colorScheme]),
        Line2: makeTextPatch(line2, wordWrapWidth, LINE2_COLOR[colorScheme]),
        Line3: {
          ...makeTextPatch(line3, wordWrapWidth, LINE3_COLOR[colorScheme]),
          visible: !progress.visible,
        },
        ProgressBar: {
          w: this._config.w - (getImageSize(screenSize) + TEXT_LEFT_PADDING[screenSize] + PADDING[screenSize] * 2),
          ...progress,
        },
      },
    });
  }

  get wordWrapWidth() {
    const renderingContext = this._renderingContext || RENDERING_CONTEXT.GENERAL;
    const { screenSize } = this._style;
    const {
      PRIMARY: { width: imageWidth },
    } = getTemplateEntry(imageOptions, this._itemTemplate, renderingContext, screenSize);
    const padding = TEXT_LEFT_PADDING[screenSize];
    const width = this._config.w || this.finalW;
    const safetyMargin = safetyMargins.vertical[screenSize];

    return width - imageWidth - padding - safetyMargin;
  }

  _focus() {
    this.patch(getFocusedStyle(this._style));
  }

  _unfocus() {
    this.patch(unFocusedStyle);
  }

  _handleEnter() {
    this.signal(SELECTED_SIGNAL, this._item);

    return true;
  }
}

HorizontalSquare.Templates = templates;
HorizontalSquare.Sizes = sizes;
HorizontalSquare.ImageOptions = imageOptions;
